import { useEffect, useMemo, useState } from "react";
import { siteStats, updateTimeline } from "@/mocks/home";

function useNextUpdate() {
  const nodes = useMemo(
    () =>
      updateTimeline.map((item) => {
        const [h, m] = item.time.split(":").map(Number);
        return { time: item.time, h, m };
      }),
    [],
  );

  const getTarget = () => {
    const now = new Date();
    const today = nodes
      .map((n) => {
        const d = new Date();
        d.setHours(n.h, n.m, 0, 0);
        return d;
      })
      .find((d) => d.getTime() > now.getTime());
    if (today) return { target: today, label: "今天", time: `${String(today.getHours()).padStart(2, "0")}:${String(today.getMinutes()).padStart(2, "0")}` };
    const first = nodes[0];
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(first.h, first.m, 0, 0);
    return { target: d, label: "明天", time: first.time };
  };

  const [state, setState] = useState(() => {
    const { target, label, time } = getTarget();
    return { diff: Math.max(target.getTime() - Date.now(), 0), label, time };
  });

  useEffect(() => {
    const tick = () => {
      const { target, label, time } = getTarget();
      setState({ diff: Math.max(target.getTime() - Date.now(), 0), label, time });
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, []);

  const h = Math.floor(state.diff / 3600000);
  const m = Math.floor((state.diff % 3600000) / 60000);
  const s = Math.floor((state.diff % 60000) / 1000);

  return {
    label: state.label,
    time: state.time,
    clock: `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`,
  };
}

export default function HeroSection() {
  const next = useNextUpdate();

  return (
    <section id="top" className="relative overflow-hidden border-b border-background-200">
      <div
        className="absolute inset-0 opacity-70"
        style={{
          backgroundImage:
            "linear-gradient(oklch(var(--background-200) / 0.55) 1px, transparent 1px), linear-gradient(90deg, oklch(var(--background-200) / 0.55) 1px, transparent 1px)",
          backgroundSize: "44px 44px",
        }}
      />
      <div
        className="absolute -right-24 -top-24 h-[420px] w-[420px] rounded-full opacity-40 blur-3xl"
        style={{ background: "radial-gradient(circle, oklch(var(--accent-200) / 0.9), transparent 70%)" }}
      />
      <div className="relative mx-auto w-full max-w-6xl px-4 py-14 md:px-8 md:py-20">
        <div className="grid grid-cols-1 items-start gap-10 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-7">
            <span className="inline-flex items-center gap-2 rounded-full border border-primary-200 bg-primary-50 px-3 py-1.5 text-xs font-medium text-primary-700">
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-live-line"></i>
              </span>
              今日更新进行中 · 已发布 3 个节点
            </span>

            <h2 className="mt-6 font-heading text-3xl font-bold leading-tight tracking-tight text-foreground-950 md:text-5xl md:leading-[1.15]">
              黑料每日几点更新，
              <br className="hidden md:block" />
              一张节奏表给你说清楚
            </h2>

            <p className="mt-5 max-w-xl text-sm leading-7 text-foreground-600 md:text-base">
              不用再靠感觉蹲守。六个固定更新节点全部列出来，配上更新提醒和追更日历，
              打卡订阅之后，重点场次开更的第一时间就能收到推送，吃瓜不迷路。
            </p>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <a
                href="#reminder"
                className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 dark:text-foreground-950"
              >
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-notification-3-line"></i>
                </span>
                设置更新提醒
              </a>
              <a
                href="#calendar"
                className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-6 py-3 text-sm font-medium text-foreground-800 transition-colors hover:border-secondary-300 hover:bg-secondary-50"
              >
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-calendar-check-line"></i>
                </span>
                打开追更日历打卡
              </a>
            </div>

            <dl className="mt-10 grid grid-cols-2 gap-x-6 gap-y-5 border-t border-background-200 pt-8 sm:grid-cols-4">
              {siteStats.map((stat) => (
                <div key={stat.id}>
                  <dd className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                    {stat.value}
                    <span className="ml-0.5 text-sm font-medium text-foreground-500">{stat.unit}</span>
                  </dd>
                  <dt className="mt-1.5 text-xs leading-5 text-foreground-500">{stat.label}</dt>
                </div>
              ))}
            </dl>
          </div>

          <div className="lg:col-span-5">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-foreground-800">距离下一次更新</span>
                <span className="rounded-full bg-accent-100 px-2.5 py-1 text-xs font-medium text-accent-900">
                  {next.label} {next.time}
                </span>
              </div>
              <div className="mt-4 flex items-baseline gap-1 font-heading text-4xl font-bold tabular-nums text-primary-600 md:text-5xl">
                {next.clock}
              </div>
              <p className="mt-3 text-xs leading-6 text-foreground-500">
                倒计时只对应当前时段的下一个节点。重点场次为 19:00 晚间主推，建议单独开启提醒。
              </p>
              <div className="mt-5 space-y-2.5 border-t border-background-200 pt-5">
                {updateTimeline.slice(0, 4).map((item) => (
                  <div key={item.id} className="flex items-center gap-3">
                    <span
                      className={`h-2 w-2 shrink-0 rounded-full ${
                        item.status === "published" ? "bg-secondary-500" : "bg-background-300"
                      }`}
                    />
                    <span className="w-14 shrink-0 text-sm tabular-nums text-foreground-800">
                      {item.time}
                    </span>
                    <span className="truncate text-xs text-foreground-500">{item.title}</span>
                    <span
                      className={`ml-auto shrink-0 text-xs ${
                        item.status === "published" ? "text-secondary-700" : "text-foreground-400"
                      }`}
                    >
                      {item.status === "published" ? "已发布" : "待更新"}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}